import React, { useState, useEffect } from 'react';
import { FeatureGate, SubFeatureGate } from '../components/FeatureGate';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '../api';
import Loader from '../components/Loader';
import { LayoutDashboard, Briefcase, Users, Award, CheckCircle, Bell, Calendar, CalendarCheck, FileText, ExternalLink, GitBranch, Terminal, X, ArrowLeft, MessageSquare, Building2, ListChecks, Search, Info, Inbox, Plus, Trash2, Loader2, TrendingUp, Brain } from 'lucide-react';
import UniAiReports from '../components/university/UniAiReports';

import StudentProfileDetail from '../components/StudentProfileDetail';
import TpoAnalyticsDashboard from '../components/university/TpoAnalyticsDashboard';
import PlacementDrivesModule from '../components/PlacementDrivesModule';
import AppliedCandidatesModule from '../components/AppliedCandidatesModule';
import FormattedResumeSummary from '../components/FormattedResumeSummary';
import AIPlacementRecommendationCard from '../components/AIPlacementRecommendationCard';
import AiDisclaimerCard from '../components/AiDisclaimerCard';
import InternalJobsModule from '../components/university/InternalJobsModule';
import './FacultyDashboard.css';
import './TpoDashboard.css';
import './UniDashboard.css';
import ChatPage from './ChatPage';

import CalendarLib from 'react-calendar';
import 'react-calendar/dist/Calendar.css';

const TpoDashboard = () => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [showCorporatePartners, setShowCorporatePartners] = useState(false);
  
  const [showNotifs, setShowNotifs] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [showCalendarModal, setShowCalendarModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date());

  const [toast, setToast] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // AI Disclaimer gate
  const [showAiDisclaimer, setShowAiDisclaimer] = useState(false);
  const [aiDisclaimerFeature, setAiDisclaimerFeature] = useState('');
  const [pendingAiAction, setPendingAiAction] = useState(null);

  const requireAiDisclaimer = (featureName, action) => {
    if (sessionStorage.getItem('ai_disclaimer_agreed')) {
      action();
    } else {
      setAiDisclaimerFeature(featureName);
      setPendingAiAction(() => action);
      setShowAiDisclaimer(true);
    }
  };

  useEffect(() => {
    if (location.state?.showProfile || location.state?.showSettings) {
      setActiveTab('profile');
      // Clear state to avoid infinite loops on reload
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3500);
  };

  const [availableBatches, setAvailableBatches] = useState([]);
  const [students, setStudents] = useState([]);
  const [loadingStudents, setLoadingStudents] = useState(false);
  const [studentBatchFilter, setStudentBatchFilter] = useState('All');
  const [studentSearch, setStudentSearch] = useState('');
  
  const [selectedStudentProfile, setSelectedStudentProfile] = useState(null);
  const [studentCourses, setStudentCourses] = useState([]);
  const [loadingStudentCourses, setLoadingStudentCourses] = useState(false);

  const [appliedCandidates, setAppliedCandidates] = useState([]);
  const [syncingApplications, setSyncingApplications] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [totalDbJobs, setTotalDbJobs] = useState(0);
  const [selectedAppliedCompany, setSelectedAppliedCompany] = useState(null);
  const [selectedAppliedRole, setSelectedAppliedRole] = useState(null);
  const [loadingApplied, setLoadingApplied] = useState(false);
  const [selectedApplication, setSelectedApplication] = useState(null);
  
  const [applicationsViewMode, setApplicationsViewMode] = useState('grouped');
  const [candidateSearch, setCandidateSearch] = useState('');
  const [candidateStatusFilter, setCandidateStatusFilter] = useState('All');
  const [candidateCompanyFilter, setCandidateCompanyFilter] = useState('All');
  const [candidateRoleFilter, setCandidateRoleFilter] = useState('All');
  const [remarksModalOpen, setRemarksModalOpen] = useState(false);
  const [remarksAppId, setRemarksAppId] = useState(null);
  const [remarksText, setRemarksText] = useState('');
  const [savingRemarks, setSavingRemarks] = useState(false);
  
  // Navigation state for Deep Linking to specific Job Drive
  const [targetDriveJobId, setTargetDriveJobId] = useState(null);
  const [unreadMessagesCount, setUnreadMessagesCount] = useState(0);

  const fetchUnreadMessages = async () => {
    try {
      const res = await api.get('/chat/conversations');
      const totalUnread = res.data.reduce((acc, curr) => acc + (curr.unread_count || 0), 0);
      setUnreadMessagesCount(totalUnread);
    } catch (err) {
      console.error("Failed to fetch unread messages", err);
    }
  };

  useEffect(() => {
    fetchProfile();
    fetchStudents();
    fetchAppliedCandidates();
    fetchJobs();
    fetchNotifications();
    fetchUnreadMessages();
    const interval = setInterval(fetchUnreadMessages, 60000);
    return () => clearInterval(interval);
  }, []);

  const fetchNotifications = async () => {
    try {
      const res = await api.get('/compliance/notifications');
      setNotifications(res.data || []);
    } catch (err) {
      console.error('Error fetching notifications', err);
    }
  };

  const handleOpenNotifs = async () => {
    const opening = !showNotifs;
    setShowNotifs(opening);
    if (opening) {
      await fetchNotifications();
    }
  };

  const fetchJobs = async () => {
    try {
      const res = await api.get('/tpo/jobs');
      const formattedJobs = (res.data || []).map(job => ({
        ...job,
        company_name: job.company_name ? job.company_name.replace(/\b\w/g, l => l.toUpperCase()) : job.company_name
      }));
      setJobs(formattedJobs);
      
      const countRes = await api.get('/tpo/jobs/total-count');
      setTotalDbJobs(countRes.data?.total || 0);
    } catch (err) {
      console.error("Error fetching jobs", err);
    }
  };

  const fetchAppliedCandidates = async () => {
    setLoadingApplied(true);
    try {
      const res = await api.get('/tpo/applied-candidates');
      const formattedCandidates = (res.data || []).map(candidate => ({
        ...candidate,
        company_name: candidate.company_name ? candidate.company_name.replace(/\b\w/g, l => l.toUpperCase()) : candidate.company_name
      }));
      setAppliedCandidates(formattedCandidates);
    } catch (err) {
      console.error("Error fetching applied candidates", err);
    } finally {
      setLoadingApplied(false);
    }
  };

  const handleSyncApplications = async () => {
    setSyncingApplications(true);
    try {
      const res = await api.post('/tpo/applications/mantrika-sync');
      showToast(`Synced applications successfully.`, 'success');
      fetchAppliedCandidates();
    } catch (err) {
      console.error("Error syncing applications", err);
      showToast(err.response?.data?.detail || "Failed to sync applications", 'error');
    } finally {
      setSyncingApplications(false);
    }
  };

  const getStatusStyle = (statusStr) => {
    const s = (statusStr || 'APPLIED').toUpperCase();
    if (s === 'APPROVED') {
      return { background: 'rgba(16, 185, 129, 0.15)', color: '#059669', border: '1px solid rgba(16, 185, 129, 0.4)' };
    }
    if (s === 'UNDER REVIEW' || s === 'UNDER_REVIEW') {
      return { background: 'rgba(245, 158, 11, 0.15)', color: '#d97706', border: '1px solid rgba(245, 158, 11, 0.4)' };
    }
    if (s === 'REMARKED') {
      return { background: 'rgba(249, 115, 22, 0.15)', color: '#ea580c', border: '1px solid rgba(249, 115, 22, 0.4)' };
    }
    if (s === 'PENDING') {
      return { background: 'rgba(234, 179, 8, 0.15)', color: '#ca8a04', border: '1px solid rgba(234, 179, 8, 0.4)' };
    }
    if (s === 'REJECTED') {
      return { background: 'rgba(239, 68, 68, 0.15)', color: '#dc2626', border: '1px solid rgba(239, 68, 68, 0.4)' };
    }
    if (s === 'WITHDRAWN') {
      return { background: 'rgba(100, 116, 139, 0.15)', color: '#475569', border: '1px solid rgba(100, 116, 139, 0.4)' };
    }
    if (s === 'INTERVIEW') {
      return { background: 'rgba(139, 92, 246, 0.15)', color: '#7c3aed', border: '1px solid rgba(139, 92, 246, 0.4)' };
    }
    return { background: 'rgba(59, 130, 246, 0.15)', color: '#3b82f6', border: '1px solid rgba(59, 130, 246, 0.4)' };
  };

  const handleUpdateAppStatus = async (appId, newStatus) => {
    try {
      if (newStatus === 'APPROVED') {
        await api.post(`/tpo/applications/${appId}/approve`);
        showToast('Application approved successfully!', 'success');
      } else {
        await api.post(`/tpo/applications/${appId}/status`, { status: newStatus });
        showToast(`Application status updated to ${newStatus}!`, 'success');
      }
      fetchAppliedCandidates();
      if (selectedApplication && selectedApplication.id === appId) {
        setSelectedApplication({ ...selectedApplication, status: newStatus });
      }
    } catch (err) {
      console.error("Error updating application status", err);
      showToast(err.response?.data?.detail || "Failed to update status", 'error');
    }
  };

  const openRemarksModal = (app) => {
    setRemarksAppId(app.id);
    setRemarksText(app.remarks || '');
    setRemarksModalOpen(true);
  };

  const handleSaveRemarks = async () => {
    const cleanRemarks = remarksText.trim();
    if (!cleanRemarks) {
      showToast("Remarks cannot be empty.", 'error');
      return;
    }
    if (cleanRemarks.length > 500) {
      showToast("Remarks cannot exceed 500 characters.", 'error');
      return;
    }
    setSavingRemarks(true);
    try {
      const res = await api.post(`/tpo/applications/${remarksAppId}/remarks`, { remarks: cleanRemarks });
      showToast("Remarks saved successfully!", 'success');
      fetchAppliedCandidates();
      if (selectedApplication && selectedApplication.id === remarksAppId) {
        setSelectedApplication({
          ...selectedApplication,
          status: res.data.status || selectedApplication.status,
          remarks: res.data.remarks,
          remarks_added_by: res.data.remarks_added_by,
          remarks_added_at: res.data.remarks_added_at
        });
      }
      setRemarksModalOpen(false);
    } catch (err) {
      console.error("Error saving remarks", err);
      showToast(err.response?.data?.detail || "Failed to save remarks", 'error');
    } finally {
      setSavingRemarks(false);
    }
  };

  // Fetches strictly needed by TpoDashboard


  const getBatchLabel = (student) => {
    const profile = student.student_profile || student;
    const parts = [];
    if (profile.graduation_level) parts.push(profile.graduation_level.trim());
    if (profile.program) parts.push(profile.program.trim());
    if (profile.branch) parts.push(profile.branch.trim());
    
    const base = parts.join(' - ');
    const yearPart = profile.academic_batch || profile.graduation_year || '';
    
    if (base && yearPart) return `${base} (${yearPart})`;
    if (base) return base;
    if (yearPart) return yearPart;
    return "Unknown Batch";
  };

  const fetchStudents = async () => {
    setLoadingStudents(true);
    try {
      const res = await api.get('/students'); // Allowed for TPOs
      const data = res.data || [];
      setStudents(data);
      
      const batches = new Set();
      data.forEach(s => {
        const b = getBatchLabel(s);
        if (b && b !== "Unknown Batch") batches.add(b);
      });
      setAvailableBatches(Array.from(batches).sort().map(b => ({ label: b, value: b })));
    } catch (err) {
      console.error("Error fetching students", err);
    } finally {
      setLoadingStudents(false);
    }
  };

  const handleViewStudentProfile = async (student) => {
    setSelectedStudentProfile(student);
    const studentId = student.user_id || student.id;
    if (studentId) {
      try {
        setLoadingStudentCourses(true);
        const res = await api.get(`/faculty/students/${studentId}/courses-and-marks`);
        setStudentCourses(res.data);
      } catch (err) {
        console.error('Error fetching student courses:', err);
        setStudentCourses([]);
      } finally {
        setLoadingStudentCourses(false);
      }
    } else {
      setStudentCourses([]);
    }
  };

  const fetchProfile = async () => {
    try {
      const res = await api.get('/profile/me');
      setProfile(res.data);
    } catch (err) {
      console.error("Error fetching TPO profile", err);
    } finally {
      setLoading(false);
    }
  };

  const corporatePartnersStats = React.useMemo(() => {
    const companyEngagement = {};
    jobs.forEach(j => {
      const comp = j.company_name || 'Unknown Partner';
      if (!companyEngagement[comp]) companyEngagement[comp] = { company: comp, jobsPosted: 0, applications: 0, approvedJobs: 0, rejectedJobs: 0 };
      companyEngagement[comp].jobsPosted += 1;
      const s = (j.status || '').toUpperCase();
      if (s === 'APPROVED') companyEngagement[comp].approvedJobs += 1;
      else if (s === 'REJECTED') companyEngagement[comp].rejectedJobs += 1;
    });
    appliedCandidates.forEach(app => {
      const comp = app.company_name || 'Unknown Partner';
      if (!companyEngagement[comp]) companyEngagement[comp] = { company: comp, jobsPosted: 0, applications: 0, approvedJobs: 0, rejectedJobs: 0 };
      companyEngagement[comp].applications += 1;
    });
    return Object.values(companyEngagement).filter(c => c.company !== 'Unknown Partner').sort((a,b) => b.jobsPosted - a.jobsPosted);
  }, [jobs, appliedCandidates]);

  if (loading) return <Loader />;

  return (
    <>
    {/* AI Disclaimer Modal — shown once per session before any AI feature */}
    {showAiDisclaimer && (
      <AiDisclaimerCard
        featureName={aiDisclaimerFeature}
        onAgree={() => {
          sessionStorage.setItem('ai_disclaimer_agreed', '1');
          setShowAiDisclaimer(false);
          if (pendingAiAction) {
            pendingAiAction();
            setPendingAiAction(null);
          }
        }}
        onCancel={() => {
          setShowAiDisclaimer(false);
          setPendingAiAction(null);
        }}
      />
    )}
    <div className="dashboard-wrapper faculty-dashboard tpo-dashboard">
      <aside className="sidebar">


        <nav className="sidebar-nav">
          <button className={activeTab === 'dashboard' ? 'active' : ''} onClick={() => setActiveTab('dashboard')}>
            <LayoutDashboard size={20} /> Dashboard
          </button>
          <button className={activeTab === 'students' ? 'active' : ''} onClick={() => setActiveTab('students')}>
            <Users size={20} /> Student Candidates
          </button>
          <button className={activeTab === 'drives' ? 'active' : ''} onClick={() => setActiveTab('drives')}>
            <Award size={20} /> Placement Drives
          </button>
          <SubFeatureGate feature="internal_jobs" sub="internal_jobs_manage">
            <button className={activeTab === 'internal_jobs' ? 'active' : ''} onClick={() => setActiveTab('internal_jobs')}>
              <Briefcase size={20} /> Campus Jobs
            </button>
          </SubFeatureGate>
          <button className={activeTab === 'applied_candidates' ? 'active' : ''} onClick={() => { setActiveTab('applied_candidates'); setSelectedApplication(null); setSelectedAppliedCompany(null); setSelectedAppliedRole(null); fetchAppliedCandidates(); }}>

            <CalendarCheck size={20} /> Applied Candidates
          </button>
          
          <button className={activeTab === 'corporate_partners' ? 'active' : ''} onClick={() => setActiveTab('corporate_partners')}>
            <Building2 size={20} /> Corporate Partners
          </button>

          <FeatureGate feature="ai_student_reports">
            <button className={activeTab === 'ai_reports' ? 'active' : ''} onClick={() => setActiveTab('ai_reports')}>
              <Brain size={20} /> AI Student Reports
            </button>
          </FeatureGate>

          <FeatureGate feature="messages">
<button className={activeTab === 'chat' ? 'active' : ''} onClick={() => setActiveTab('chat')}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                <MessageSquare size={20} /> Messages
              </div>
              {unreadMessagesCount > 0 && (
                <span style={{
                  background: '#ef4444',
                  color: 'white',
                  borderRadius: '50%',
                  padding: '2px 8px',
                  fontSize: '11px',
                  fontWeight: 'bold',
                  minWidth: '20px',
                  textAlign: 'center'
                }}>
                  {unreadMessagesCount}
                </span>
              )}
            </div>
          </button>
</FeatureGate>
        </nav>
      </aside>

      <main className="main-content">
        <header className="top-bar">
          <h1>
            {activeTab === 'dashboard' ? 'TPO Dashboard' :
              activeTab === 'students' ? 'Student Candidates' :
              activeTab === 'drives' ? 'Placement Drives' :
              activeTab === 'internal_jobs' ? 'Campus Job Postings' :
              activeTab === 'ai_reports' ? 'AI Student Reports' :
              activeTab === 'profile' ? 'Profile & Settings' :
              activeTab === 'corporate_partners' ? 'Corporate Partners' :

              activeTab === 'applied_candidates' ? (selectedApplication ? 'Candidate Application Details' : (applicationsViewMode === 'all' ? 'All Applied Candidates' : (selectedAppliedRole ? `${selectedAppliedRole} Applicants` : (selectedAppliedCompany ? `${selectedAppliedCompany} Roles` : 'Applied Candidates')))) :
              'TPO Dashboard'}
          </h1>
          <div className="top-bar-right">
            <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
              <button 
                title="Notifications" 
                onClick={handleOpenNotifs}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--faculty-muted)', position: 'relative', marginRight: '16px', zIndex: 10 }}

              >
                <Bell size={22} />
                {notifications.some(n => !n.is_read) && (
                  <span style={{
                    position: 'absolute', top: '-2px', right: '-2px',
                    width: '10px', height: '10px', background: '#ef4444',
                    borderRadius: '50%', border: '2px solid white'
                  }}></span>
                )}
              </button>
              
              {showNotifs && (
                <div className="uni-card" style={{
                  position: 'absolute', top: '40px', right: '10px', width: '320px',
                  zIndex: 100, padding: '16px', border: '1px solid var(--border)',
                  boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)', background: 'var(--bg-card)', borderRadius: '12px', textAlign: 'left'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                    <h4 style={{ fontSize: '14px', fontWeight: 600, margin: 0 }}>Notifications</h4>
                    <button onClick={() => setShowNotifs(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px' }}>
                      <X size={16} color="var(--text-muted)" />
                    </button>
                  </div>
                  {/* Mark all as read button */}
                  {notifications.some(n => !n.is_read) && (
                    <button
                      onClick={async () => {
                        try {
                          await api.post('/compliance/notifications/mark-all-read');
                          setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
                        } catch (err) { console.error(err); }
                      }}
                      style={{ width: '100%', background: 'none', border: 'none', color: 'var(--primary)', fontSize: '12px', cursor: 'pointer', textAlign: 'right', padding: '0 0 10px 0', fontWeight: 600 }}
                    >
                      Mark all as read
                    </button>
                  )}
                  <div style={{ maxHeight: '320px', overflowY: 'auto' }}>
                    {notifications.filter(n => !n.is_read).length > 0
                      ? notifications.filter(n => !n.is_read).map(n => (
                        <div key={n.id} style={{ padding: '10px 12px', borderBottom: '1px solid var(--border)', fontSize: '13px', display: 'flex', alignItems: 'flex-start', gap: '10px', background: 'rgba(59,130,246,0.04)', borderRadius: '6px', marginBottom: '4px' }}>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: 700, color: 'var(--text)' }}>{n.title}</div>
                            <div style={{ color: 'var(--text-muted)', marginTop: '3px', lineHeight: 1.4 }}>{n.message}</div>
                            <div style={{ fontSize: '11px', color: 'var(--text-faint)', marginTop: '5px' }}>{new Date(n.created_at).toLocaleString()}</div>
                          </div>
                          <button
                            title="Mark as read"
                            onClick={async () => {
                              try {
                                await api.post(`/compliance/notifications/${n.id}/mark-read`);
                                setNotifications(prev => prev.map(x => x.id === n.id ? { ...x, is_read: true } : x));
                              } catch (err) { console.error(err); }
                            }}
                            style={{ background: 'none', border: '1px solid var(--border)', borderRadius: '6px', cursor: 'pointer', padding: '4px 8px', fontSize: '11px', color: 'var(--text-muted)', whiteSpace: 'nowrap', flexShrink: 0, marginTop: '2px' }}
                          >
                            ✓ Done
                          </button>
                        </div>
                      ))
                      : <p style={{ fontSize: '13px', color: 'var(--text-muted)', textAlign: 'center', padding: '20px 10px' }}>🎉 You're all caught up!</p>
                    }
                  </div>
                </div>
              )}

            </div>

            <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
              <button 
                className={`faculty-btn-secondary ${showCalendarModal ? 'active' : ''}`}
                onClick={() => setShowCalendarModal(!showCalendarModal)}
                style={{ display: 'flex', alignItems: 'center', gap: '8px', zIndex: 10 }}
                title="Calendar View"
              >
                <Calendar size={16} />
              </button>
              
              {showCalendarModal && (
                <div className="uni-card" style={{
                  position: 'absolute', top: '40px', right: 0,
                  zIndex: 100, padding: '16px', border: '1px solid var(--border)',
                  boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)', background: 'var(--bg-card)', borderRadius: '12px'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                    <h4 style={{ fontSize: '14px', fontWeight: 600, margin: 0 }}>Calendar</h4>
                    <button onClick={() => setShowCalendarModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px' }}>
                      <X size={16} color="var(--text-muted)" />
                    </button>
                  </div>
                  <CalendarLib
                    onChange={setSelectedDate}
                    value={selectedDate}
                  />
                </div>
              )}
            </div>
            
          </div>
        </header>

        <section className="dashboard-content faculty-dashboard-content">
          <div className="unidash-fade-in" style={{ paddingBottom: '40px' }}>
            {activeTab === 'dashboard' && (
              <TpoAnalyticsDashboard 
                students={students} 
                jobs={jobs} 
                totalDbJobs={totalDbJobs}
                appliedCandidates={appliedCandidates} 
                profile={profile} 
                setActiveTab={setActiveTab} 
                setTargetDriveJobId={setTargetDriveJobId}
              />
            )}

            {activeTab === 'students' && (
              <div>
                {selectedStudentProfile ? (
                  <StudentProfileDetail
                    student={selectedStudentProfile}
                    onBack={() => setSelectedStudentProfile(null)}
                    coursesData={studentCourses}
                    loadingCourses={loadingStudentCourses}
                  />
                ) : (
                  <>
                    <div className="faculty-page-heading" style={{ marginBottom: '24px' }}>
                      <h2>Students Directory</h2>
                      <p>View and filter all students enrolled in your university.</p>
                    </div>

                    <div className="tpo-students-toolbar" style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '20px', flexWrap: 'wrap' }}>
                      <div style={{ position: 'relative', flex: '1', minWidth: '220px', maxWidth: '360px' }}>
                        <span style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--faculty-muted)', display: 'flex' }}>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
                        </span>
                        <input
                          type="text"
                          placeholder="Search by name, USN, or email..."
                          value={studentSearch}
                          onChange={e => setStudentSearch(e.target.value)}
                          style={{ width: '100%', padding: '10px 12px 10px 38px', border: '1px solid var(--faculty-border)', borderRadius: '8px', fontSize: '0.9rem', color: 'var(--faculty-text)', background: '#fff', outline: 'none', boxSizing: 'border-box' }}
                        />
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginLeft: 'auto' }}>
                        <span style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.85rem', color: 'var(--faculty-muted)', fontWeight: '600', whiteSpace: 'nowrap' }}>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/></svg>
                          Filter by Batch:
                        </span>
                        <select
                          style={{ padding: '8px 12px', border: '1px solid var(--faculty-border)', borderRadius: '8px', fontSize: '0.88rem', color: 'var(--faculty-text)', background: '#fff', minWidth: '160px', cursor: 'pointer' }}
                          value={studentBatchFilter}
                          onChange={e => setStudentBatchFilter(e.target.value)}
                        >
                          <option value="All">All</option>
                          {availableBatches.map(batch => (
                            <option key={batch.label} value={batch.value}>{batch.label}</option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div className="tpo-students-table-wrap" style={{ background: '#fff', border: '1px solid var(--faculty-border)', borderRadius: '12px', overflow: 'hidden' }}>
                      {loadingStudents ? (
                        <div style={{ padding: '48px', textAlign: 'center', color: 'var(--faculty-muted)' }}>Loading candidates...</div>
                      ) : (() => {
                        const filtered = students.filter(s => {
                          const matchBatch = studentBatchFilter === 'All' || getBatchLabel(s) === studentBatchFilter;
                          const q = studentSearch.toLowerCase();
                          const matchSearch = !q || (s.full_name || '').toLowerCase().includes(q) || (s.email || '').toLowerCase().includes(q) || (s.usn || '').toLowerCase().includes(q);
                          return matchBatch && matchSearch;
                        });
                        if (filtered.length === 0) return (
                          <div style={{ textAlign: 'center', padding: '56px 20px' }}>
                            <Users size={40} style={{ color: 'var(--faculty-muted)', marginBottom: '12px' }} />
                            <p style={{ color: 'var(--faculty-muted)', fontWeight: '600' }}>No students found.</p>
                          </div>
                        );
                        return (
                          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                              <tr style={{ background: 'var(--faculty-bg)', borderBottom: '1px solid var(--faculty-border)' }}>
                                {['STUDENT', 'USN', 'DEPARTMENT', 'BATCH', 'DETAILS'].map(col => (
                                  <th key={col} style={{ padding: '12px 20px', textAlign: 'left', fontSize: '0.75rem', fontWeight: '700', color: 'var(--faculty-muted)', letterSpacing: '0.6px' }}>{col}</th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {filtered.map((student, idx) => {
                                const name = student.full_name || 'Unknown';
                                const initials = name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
                                const colors = ['#3b82f6','#10b981','#f59e0b','#8b5cf6','#ef4444','#06b6d4'];
                                const color = colors[name.charCodeAt(0) % colors.length];
                                return (
                                  <tr key={student.id} style={{ borderBottom: idx < filtered.length - 1 ? '1px solid var(--faculty-border)' : 'none', transition: 'background 0.15s' }}
                                    onMouseEnter={e => e.currentTarget.style.background = 'var(--faculty-bg)'}
                                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                                  >
                                    <td style={{ padding: '14px 20px' }}>
                                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                        <div style={{ width: '36px', height: '36px', borderRadius: '8px', background: color, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: '700', fontSize: '0.85rem', flexShrink: 0 }}>{initials}</div>
                                        <div>
                                          <div style={{ fontWeight: '600', color: 'var(--faculty-text)', fontSize: '0.9rem' }}>{name}</div>
                                          <div style={{ fontSize: '0.78rem', color: 'var(--faculty-muted)', display: 'flex', alignItems: 'center', gap: '4px', marginTop: '2px' }}>
                                            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect width="20" height="16" x="2" y="4" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></svg>
                                            {student.email}
                                          </div>
                                        </div>
                                      </div>
                                    </td>
                                    <td style={{ padding: '14px 20px', color: 'var(--faculty-text)', fontWeight: '500', fontSize: '0.88rem' }}>{student.usn || '—'}</td>
                                    <td style={{ padding: '14px 20px' }}>
                                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: 'var(--faculty-muted)', fontSize: '0.88rem' }}>
                                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/></svg>
                                        {student.branch || student.degree || '—'}
                                      </div>
                                    </td>
                                    <td style={{ padding: '14px 20px' }}>
                                      <span style={{ background: 'var(--faculty-accent-soft)', color: 'var(--faculty-accent)', padding: '3px 10px', borderRadius: '20px', fontSize: '0.78rem', fontWeight: '700', whiteSpace: 'nowrap' }}>
                                        {student.academic_batch || student.graduation_year || '—'}
                                      </span>
                                    </td>
                                    <td style={{ padding: '14px 20px' }}>
                                      <button
                                        onClick={() => handleViewStudentProfile(student)}
                                        style={{ display: 'flex', alignItems: 'center', gap: '5px', padding: '6px 14px', border: '1px solid var(--faculty-border)', borderRadius: '6px', background: '#fff', color: 'var(--faculty-text)', fontSize: '0.82rem', fontWeight: '600', cursor: 'pointer', transition: 'all 0.15s' }}
                                        onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--faculty-accent)'; e.currentTarget.style.color = 'var(--faculty-accent)'; }}
                                        onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--faculty-border)'; e.currentTarget.style.color = 'var(--faculty-text)'; }}
                                      >
                                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z"/><circle cx="12" cy="12" r="3"/></svg>
                                        View
                                      </button>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        );
                      })()}
                    </div>
                  </>
                )}
              </div>
            )}

            {activeTab === 'drives' && (
              <PlacementDrivesModule 
                showToast={showToast} 
                targetDriveJobId={targetDriveJobId}
                setTargetDriveJobId={setTargetDriveJobId}
                onViewCandidates={(company, role) => {
                  setActiveTab('applied_candidates');
                  setSelectedApplication(null);
                  setSelectedAppliedCompany(company);
                  setSelectedAppliedRole(role);
                  setApplicationsViewMode('grouped');
                  fetchAppliedCandidates();
                }}
              />
            )}

            {activeTab === 'internal_jobs' && (
              <SubFeatureGate feature="internal_jobs" sub="internal_jobs_manage">
                <InternalJobsModule showToast={showToast} />
              </SubFeatureGate>
            )}


            {activeTab === 'applied_candidates' && (
              <AppliedCandidatesModule
                appliedCandidates={appliedCandidates}
                selectedApplication={selectedApplication}
                setSelectedApplication={setSelectedApplication}
                selectedAppliedCompany={selectedAppliedCompany}
                setSelectedAppliedCompany={setSelectedAppliedCompany}
                selectedAppliedRole={selectedAppliedRole}
                setSelectedAppliedRole={setSelectedAppliedRole}
                loadingApplied={loadingApplied}
                applicationsViewMode={applicationsViewMode}
                setApplicationsViewMode={setApplicationsViewMode}
                candidateSearch={candidateSearch}
                setCandidateSearch={setCandidateSearch}
                candidateCompanyFilter={candidateCompanyFilter}
                setCandidateCompanyFilter={setCandidateCompanyFilter}
                candidateRoleFilter={candidateRoleFilter}
                setCandidateRoleFilter={setCandidateRoleFilter}
                candidateStatusFilter={candidateStatusFilter}
                setCandidateStatusFilter={setCandidateStatusFilter}
                handleUpdateAppStatus={handleUpdateAppStatus}
                openRemarksModal={openRemarksModal}
                getStatusStyle={getStatusStyle}
                handleSyncApplications={handleSyncApplications}
                syncingApplications={syncingApplications}
              />
            )}

            {activeTab === 'profile' && (
              <section className="faculty-panel fade-in">
                <div className="panel-header">
                  <h2>TPO Profile Settings</h2>
                  <p>Manage your university placement officer profile details.</p>
                </div>
                <div style={{ padding: '40px', color: 'var(--faculty-text)' }}>
                  {profile ? (
                    <div style={{ padding: '30px', background: 'var(--faculty-bg)', borderRadius: '12px', border: '1px solid var(--faculty-border)', maxWidth: '500px' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                        <div>
                          <strong style={{ display: 'block', color: 'var(--faculty-muted)', fontSize: '0.85rem', marginBottom: '4px' }}>Full Name</strong>
                          <div style={{ fontSize: '1.1rem', fontWeight: 600 }}>{profile.full_name || 'N/A'}</div>
                        </div>
                        <div>
                          <strong style={{ display: 'block', color: 'var(--faculty-muted)', fontSize: '0.85rem', marginBottom: '4px' }}>Email Address</strong>
                          <div style={{ fontSize: '1.1rem' }}>{profile.email}</div>
                        </div>
                        <div>
                          <strong style={{ display: 'block', color: 'var(--faculty-muted)', fontSize: '0.85rem', marginBottom: '4px' }}>Role</strong>
                          <div style={{ fontSize: '1.1rem', textTransform: 'capitalize' }}>{profile.role}</div>
                        </div>
                        <div>
                          <strong style={{ display: 'block', color: 'var(--faculty-muted)', fontSize: '0.85rem', marginBottom: '4px' }}>Phone Number</strong>
                          <div style={{ fontSize: '1.1rem' }}>{profile.phone_number || 'Not provided'}</div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <p>Loading profile...</p>
                  )}
                </div>
              </section>
            )}
            
            {activeTab === 'corporate_partners' && (
               <section className="faculty-panel fade-in">
                 <div className="panel-header">
                   <h2>Corporate Partners</h2>
                   <p>Overview of engagement with our corporate partners.</p>
                 </div>
                 <div style={{ padding: '24px' }}>
                   <div style={{ overflowX: 'auto', borderRadius: '12px', border: '1px solid var(--faculty-border, #e2e8f0)', boxShadow: '0 4px 12px rgba(0,0,0,0.02)' }}>
                     <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', background: 'var(--faculty-bg, #ffffff)' }}>
                       <thead>
                         <tr style={{ background: '#f8fafc', borderBottom: '2px solid var(--faculty-border, #e2e8f0)' }}>
                           <th style={{ padding: '16px 24px', fontWeight: '700', color: '#475569', fontSize: '0.95rem' }}>Corporate Partner</th>
                           <th style={{ padding: '16px 24px', fontWeight: '700', color: '#475569', fontSize: '0.95rem', textAlign: 'center' }}>Jobs Offered</th>
                           <th style={{ padding: '16px 24px', fontWeight: '700', color: '#475569', fontSize: '0.95rem', textAlign: 'center' }}>Approved</th>
                           <th style={{ padding: '16px 24px', fontWeight: '700', color: '#475569', fontSize: '0.95rem', textAlign: 'center' }}>Rejected</th>
                         </tr>
                       </thead>
                       <tbody>
                         {corporatePartnersStats.map((partner, idx) => (
                           <tr key={idx} style={{ borderBottom: '1px solid var(--faculty-border, #e2e8f0)', transition: 'background 0.2s' }} onMouseEnter={e => e.currentTarget.style.background = '#f1f5f9'} onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                             <td style={{ padding: '16px 24px', fontWeight: '600', color: 'var(--faculty-text, #0f172a)', fontSize: '1rem' }}>{partner.company}</td>
                             <td style={{ padding: '16px 24px', textAlign: 'center' }}>
                               <span style={{ display: 'inline-block', minWidth: '40px', color: 'var(--faculty-text, #0f172a)', background: '#e2e8f0', padding: '4px 12px', borderRadius: '12px', fontSize: '0.95rem', fontWeight: '600' }}>{partner.jobsPosted}</span>
                             </td>
                             <td style={{ padding: '16px 24px', textAlign: 'center' }}>
                               <span style={{ display: 'inline-block', minWidth: '40px', color: '#10b981', background: '#d1fae5', padding: '4px 12px', borderRadius: '12px', fontSize: '0.95rem', fontWeight: '600' }}>{partner.approvedJobs}</span>
                             </td>
                             <td style={{ padding: '16px 24px', textAlign: 'center' }}>
                               <span style={{ display: 'inline-block', minWidth: '40px', color: '#ef4444', background: '#fee2e2', padding: '4px 12px', borderRadius: '12px', fontSize: '0.95rem', fontWeight: '600' }}>{partner.rejectedJobs}</span>
                             </td>
                           </tr>
                         ))}
                         {corporatePartnersStats.length === 0 && (
                           <tr>
                             <td colSpan="4" style={{ padding: '40px', textAlign: 'center', color: '#94a3b8', fontSize: '1.05rem', background: '#ffffff' }}>No partner data available</td>
                           </tr>
                         )}
                       </tbody>
                     </table>
                   </div>
                 </div>
               </section>
            )}
            
            {activeTab === 'chat' && (
              <FeatureGate feature="messages">
                <ChatPage embedded={true} />
              </FeatureGate>
            )}

            {activeTab === 'ai_reports' && (
              <FeatureGate
                feature="ai_student_reports"
                fallback={<div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-muted)' }}>AI Student Reports feature is not enabled for your institution.</div>}
              >
                <UniAiReports showToast={showToast} />
              </FeatureGate>
            )}
          </div>
        </section>
      </main>
    </div>

      {toast && (
        <div style={{
          position: 'fixed',
          bottom: '32px',
          right: '32px',
          zIndex: 9999,
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          padding: '16px 24px',
          borderRadius: '14px',
          background: toast.type === 'success' ? 'linear-gradient(135deg, #10b981, #059669)' : 'linear-gradient(135deg, #ef4444, #dc2626)',
          color: '#fff',
          fontWeight: '600',
          fontSize: '15px',
          boxShadow: '0 8px 32px rgba(0,0,0,0.25)',
          animation: 'slideInToast 0.35s cubic-bezier(0.34,1.56,0.64,1)',
          minWidth: '280px',
          maxWidth: '420px'
        }}>
          <span style={{ fontSize: '20px' }}>
            {toast.type === 'success' ? '✅' : '❌'}
          </span>
          <span style={{ flex: 1 }}>{toast.message}</span>
          <button
            onClick={() => setToast(null)}
            style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', fontSize: '18px', opacity: 0.8, padding: 0 }}
          >×</button>
        </div>
      )}

      {/* Modals are handled by modules now */}

      {remarksModalOpen && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.55)',
          backdropFilter: 'blur(4px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000,
          padding: '20px'
        }}>
          <div style={{
            background: 'var(--faculty-bg, #ffffff)',
            borderRadius: '16px',
            width: '100%',
            maxWidth: '520px',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.25)',
            border: '1px solid var(--faculty-border, #e5e7eb)',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column'
          }}>
            <div style={{
              padding: '20px 24px',
              borderBottom: '1px solid var(--faculty-border, #e5e7eb)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              background: 'linear-gradient(to right, #eff6ff, #ffffff)'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <MessageSquare size={22} style={{ color: '#2563eb' }} />
                <h3 style={{ margin: 0, fontSize: '1.15rem', fontWeight: 700, color: 'var(--faculty-text, #111827)' }}>
                  Add Remarks
                </h3>
              </div>
              <button
                onClick={() => setRemarksModalOpen(false)}
                style={{ background: 'none', border: 'none', color: '#6b7280', cursor: 'pointer', padding: 4 }}
              >
                <X size={20} />
              </button>
            </div>

            <div style={{ padding: '24px' }}>
              <label style={{ display: 'block', fontSize: '0.88rem', fontWeight: 600, color: 'var(--faculty-text, #374151)', marginBottom: '8px' }}>
                Remarks <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <textarea
                rows={4}
                value={remarksText}
                onChange={(e) => setRemarksText(e.target.value)}
                placeholder="Enter remarks for the student..."
                style={{
                  width: '100%',
                  padding: '12px 14px',
                  borderRadius: '10px',
                  border: '1px solid var(--faculty-border, #d1d5db)',
                  background: 'var(--faculty-bg, #ffffff)',
                  color: 'var(--faculty-text, #111827)',
                  fontSize: '0.9rem',
                  lineHeight: '1.5',
                  resize: 'vertical',
                  outline: 'none',
                  fontFamily: 'inherit',
                  boxSizing: 'border-box'
                }}
              />
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.78rem', color: remarksText.trim().length > 500 ? '#ef4444' : '#6b7280', marginTop: '6px' }}>
                <span>Maximum 500 characters</span>
                <span>{remarksText.trim().length} / 500</span>
              </div>

              <div style={{ marginTop: '20px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '14px' }}>
                <span style={{ fontSize: '0.8rem', fontWeight: 700, color: '#475569', display: 'block', marginBottom: '8px' }}>
                  Examples:
                </span>
                <ul style={{ margin: 0, paddingLeft: '18px', fontSize: '0.82rem', color: '#64748b', lineHeight: '1.6' }}>
                  <li style={{ cursor: 'pointer' }} onClick={() => setRemarksText("Resume needs more projects.")}>• Resume needs more projects.</li>
                  <li style={{ cursor: 'pointer' }} onClick={() => setRemarksText("Waiting for company confirmation.")}>• Waiting for company confirmation.</li>
                  <li style={{ cursor: 'pointer' }} onClick={() => setRemarksText("Interview schedule will be shared soon.")}>• Interview schedule will be shared soon.</li>
                  <li style={{ cursor: 'pointer' }} onClick={() => setRemarksText("Please update your GitHub profile.")}>• Please update your GitHub profile.</li>
                </ul>
              </div>
            </div>

            <div style={{
              padding: '16px 24px',
              borderTop: '1px solid var(--faculty-border, #e5e7eb)',
              background: '#f8fafc',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '12px'
            }}>
              <button
                onClick={() => setRemarksModalOpen(false)}
                style={{ padding: '9px 18px', borderRadius: '8px', border: '1px solid #d1d5db', background: '#ffffff', color: '#374151', fontWeight: 600, fontSize: '0.88rem', cursor: 'pointer' }}
              >
                Cancel
              </button>
              <button
                onClick={handleSaveRemarks}
                disabled={savingRemarks || !remarksText.trim() || remarksText.trim().length > 500}
                style={{
                  padding: '9px 20px',
                  borderRadius: '8px',
                  border: 'none',
                  background: (!remarksText.trim() || remarksText.trim().length > 500 || savingRemarks) ? '#93c5fd' : 'linear-gradient(135deg, #2563eb, #1d4ed8)',
                  color: '#ffffff',
                  fontWeight: 600,
                  fontSize: '0.88rem',
                  cursor: (!remarksText.trim() || remarksText.trim().length > 500 || savingRemarks) ? 'not-allowed' : 'pointer',
                  boxShadow: (!remarksText.trim() || remarksText.trim().length > 500 || savingRemarks) ? 'none' : '0 4px 12px rgba(37, 99, 235, 0.25)'
                }}
              >
                {savingRemarks ? 'Saving...' : 'Save Remarks'}
              </button>
            </div>
          </div>
        </div>
      )}


    </>
  );
};

export default TpoDashboard;
